import { api } from "@/lib/api";

export type BriefRole = "gp" | "specialist" | "practice_staff" | "other";
export type BriefSubscriberStatus = "pending" | "confirmed" | "unsubscribed";

export interface BriefSubscribePayload {
  email: string;
  role?: BriefRole;
  practiceName?: string;
  sourcePath?: string;
}

export interface BriefTokenPeek {
  email_masked: string;
  status: BriefSubscriberStatus;
}

export interface BriefIssueSummary {
  issueDate: string;
  subject: string;
}

export interface BriefIssue extends BriefIssueSummary {
  html: string;
}

export interface BriefPreview {
  subject: string;
  html: string;
  text: string;
  ownerHtml: string | null;
  ownerText: string | null;
  ownerNote?: string | null;
}

export interface BriefPreferences {
  emailMasked: string;
  practiceOwnerUpdates: boolean;
}

export interface BriefSubscriber {
  id: string;
  email: string;
  role: BriefRole;
  practiceName: string | null;
  status: BriefSubscriberStatus;
  confirmedAt: string | null;
  lastSentAt: string | null;
  createdAt: string;
}

export interface BriefSubscribersResponse {
  count: number;
  items?: BriefSubscriber[];
  subscribers?: BriefSubscriber[];
}

export interface ContactSuppression {
  email?: string;
  emailLower?: string;
  reason: string;
  createdAt: string;
}

export interface SuppressionResponse {
  items?: ContactSuppression[];
  suppressions?: ContactSuppression[];
}

export function subscribeToBrief(citySlug: string, payload: BriefSubscribePayload) {
  return api.post<{ ok: true }>(`/v1/public/${citySlug}/brief/subscribe`, payload, {
    skipAuth: true,
  });
}

export function peekBriefConfirmation(citySlug: string, token: string) {
  return api.get<BriefTokenPeek>(
    `/v1/public/${citySlug}/brief/confirm/${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export function confirmBriefSubscription(citySlug: string, token: string) {
  return api.post<BriefTokenPeek>(
    `/v1/public/${citySlug}/brief/confirm/${encodeURIComponent(token)}`,
    undefined,
    { skipAuth: true },
  );
}

export function peekBriefUnsubscribe(citySlug: string, token: string) {
  return api.get<BriefTokenPeek>(
    `/v1/public/${citySlug}/brief/unsubscribe/${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export function unsubscribeFromBrief(citySlug: string, token: string) {
  return api.post<BriefTokenPeek>(
    `/v1/public/${citySlug}/brief/unsubscribe/${encodeURIComponent(token)}`,
    undefined,
    { skipAuth: true },
  );
}

export function getBriefPreferences(citySlug: string, token: string) {
  return api.get<BriefPreferences>(
    `/v1/public/${citySlug}/brief/preferences/${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export function updateBriefPreferences(
  citySlug: string,
  token: string,
  payload: { practiceOwnerUpdates: boolean },
) {
  return api.post<{ ok: true }>(
    `/v1/public/${citySlug}/brief/preferences/${encodeURIComponent(token)}`,
    payload,
    { skipAuth: true },
  );
}

export function getLatestBrief(citySlug: string) {
  return api.get<BriefIssue>(`/v1/public/${citySlug}/brief/latest`, {
    skipAuth: true,
  });
}

export function getBriefIssues(citySlug: string) {
  return api.get<BriefIssueSummary[]>(`/v1/public/${citySlug}/brief/issues`, {
    skipAuth: true,
  });
}

export function getBriefPreview(date: string) {
  return api.get<BriefPreview>("/v1/admin/brief/preview", {
    params: { date },
  });
}

export function saveBriefOwnerNote(date: string, text: string) {
  return api.post<{ ok: true }>("/v1/admin/brief/owner-note", { date, text });
}

export function getBriefSubscribers(status?: BriefSubscriberStatus) {
  return api.get<BriefSubscribersResponse>("/v1/admin/brief/subscribers", {
    params: { status },
  });
}

export function getContactSuppressions() {
  return api.get<SuppressionResponse | ContactSuppression[]>(
    "/v1/admin/suppression",
  );
}

export function addContactSuppression(email: string, reason: string) {
  return api.post("/v1/admin/suppression", { email, reason });
}

export function subscriberItems(response: BriefSubscribersResponse | undefined) {
  return response?.items ?? response?.subscribers ?? [];
}

export function suppressionItems(
  response: SuppressionResponse | ContactSuppression[] | undefined,
) {
  if (!response) return [];
  return Array.isArray(response)
    ? response
    : response.items ?? response.suppressions ?? [];
}
